/**
 * MediaTiles — grid/slider section powered by Splide.
 * Switches mode based on data-desktop-view / data-mobile-view attributes.
 * Supports fractional perPage (e.g. 2.3 = 2 full slides + 30% peek of next).
 */
(function () {
  const BREAKPOINT = 768;

  class MediaTiles {
    constructor(el) {
      this.el = el;
      this.d = el.dataset;
      this.splideInstance = null;
      this.currentMode = null;

      this._onResize = this._update.bind(this);
      window.addEventListener('resize', this._onResize);

      this._update();

      if (window.Shopify?.designMode) {
        el.addEventListener('shopify:section:load', () => this._update());
      }
    }

    get _isDesktop() { return window.innerWidth >= BREAKPOINT; }

    get _view() {
      return this._isDesktop ? this.d.desktopView : this.d.mobileView;
    }

    get _autoscroll() {
      return this._isDesktop
        ? this.d.autoscrollDesktop === 'true'
        : this.d.autoscrollMobile === 'true';
    }

    get _autoscrollSpeed() {
      const raw = this._isDesktop ? this.d.autoscrollSpeedDesktop : this.d.autoscrollSpeedMobile;
      return parseFloat(raw) || 1;
    }

    get _perPage() {
      return parseFloat(this._isDesktop ? this.d.desktopPerPage : this.d.mobilePerPage) || 1;
    }

    get _scrollPadding() {
      const val = parseInt(this._isDesktop ? this.d.desktopScrollPadding : this.d.mobileScrollPadding, 10);
      return val > 0 ? val + 'px' : '0px';
    }

    _update() {
      const view = this._view;
      const autoscroll = this._autoscroll;
      const stateKey = view + ':' + autoscroll;
      if (stateKey === this.currentMode) return;

      if (view === 'slider') {
        this._initSlider();
      } else {
        this._destroySlider();
        this._applyGrid();
      }
      this.currentMode = stateKey;
    }

    _calcPaddingRight(perpage) {
      const frac = perpage % 1;
      if (frac === 0) return '0%';
      // padding = frac / perpage * 100  (derived so floor slides + peek fills container)
      return (frac / perpage * 100).toFixed(3) + '%';
    }

    _initSlider() {
      this._destroySlider();

      const perpage = this._perPage;
      const perPageFloor = Math.max(1, Math.floor(perpage));
      const hasPeek = (perpage % 1) !== 0;
      const paddingRight = this._calcPaddingRight(perpage);
      const gap = this.d.gap || '16px';
      const loop = this.d.loop === 'true';
      const autoplay = this.d.autoplay === 'true';
      const autoscroll = this._autoscroll;
      const showArrows = this.d.showArrows === 'true';
      const showDots = this.d.showDots === 'true';

      const slideCount = this.el.querySelectorAll('.media-tiles--item').length;
      // AutoScroll always needs loop. Otherwise fall back to slide+rewind when peek is active.
      const useLoop = autoscroll || (loop && !hasPeek && slideCount >= perPageFloor * 2 + 1);

      // Add Splide structural classes
      this.el.classList.add('splide');
      this.el.querySelector('.media-tiles--track').classList.add('splide__track');
      this.el.querySelector('.media-tiles--list').classList.add('splide__list');
      this.el.querySelectorAll('.media-tiles--item').forEach(item => {
        item.classList.add('splide__slide');
      });

      const scrollPadding = this._scrollPadding;

      const options = {
        type: useLoop ? 'loop' : 'slide',
        rewind: !useLoop && loop,
        perPage: perPageFloor,
        perMove: 1,
        gap,
        padding: { left: scrollPadding, right: paddingRight },
        arrows: false,
        pagination: showDots,
        autoplay: autoscroll ? false : autoplay,
        interval: 4000,
        pauseOnHover: true,
        drag: autoscroll ? 'free' : true,
      };

      if (autoscroll) {
        options.autoScroll = { speed: this._autoscrollSpeed, pauseOnHover: true, pauseOnFocus: true };
      }

      const AutoScroll = window.splide?.Extensions?.AutoScroll;
      this.splideInstance = new Splide(this.el, options)
        .mount(autoscroll && AutoScroll ? { AutoScroll } : {});

      // Custom prev/next
      const prev = this.el.querySelector('.media-tiles--prev');
      const next = this.el.querySelector('.media-tiles--next');
      if (prev) {
        prev.onclick = () => this.splideInstance.go('<');
        next.onclick = () => this.splideInstance.go('>');
        prev.style.display = showArrows && !autoscroll ? '' : 'none';
        next.style.display = showArrows && !autoscroll ? '' : 'none';
      }

      this.el.classList.remove('is-grid');
      this.el.classList.add('is-slider');
    }

    _destroySlider() {
      if (!this.splideInstance) return;

      this.splideInstance.destroy(true);
      this.splideInstance = null;

      this.el.classList.remove('splide', 'splide--slide', 'splide--loop', 'splide--ltr', 'is-slider');
      this.el.querySelector('.media-tiles--track')?.classList.remove('splide__track');
      this.el.querySelector('.media-tiles--list')?.classList.remove('splide__list');
      this.el.querySelectorAll('.media-tiles--item').forEach(item => {
        item.classList.remove('splide__slide');
        item.style.width = '';
      });
    }

    _applyGrid() {
      this.el.classList.add('is-grid');
    }
  }

  function init() {
    document.querySelectorAll('.media-tiles').forEach(el => {
      if (!el.dataset.tilesInit) {
        el.dataset.tilesInit = 'true';
        new MediaTiles(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
